import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { COUNTRY_OPTIONS, isSweden as isSwedenCountry } from '../../../../../utils/vatTreatment';
import BrandSelect from '../../../Common/BrandSelect';
import InfoPopover from '../../../Common/InfoPopover';
import '../../../Common/ll-modal.css';

/**
 * The three dialogs of the customer detail page — address, contact person and
 * the customer's own details — on the shared .ll-modal chrome. They are
 * controlled: the page owns the form state and the submit handlers, these only
 * draw the fields. Each used to be ~250 lines with its own icon-and-subtitle
 * section headers and a three-tile address-type picker.
 */

const ADDRESS_TYPES = [
  { value: 'billing', label: 'Fakturaadress' },
  { value: 'shipping', label: 'Leveransadress' },
  { value: 'other', label: 'Annan adress' },
];

const COUNTRIES = COUNTRY_OPTIONS.map((c) => ({ value: c.value, label: c.label }));

const Req = () => <span className="ll-required" aria-hidden="true"> *</span>;

function Frame({ show, onClose, onSubmit, title, subtitle, submitLabel, submitting, children }) {
  const close = () => { if (!submitting) onClose(); };
  return (
    <Modal show={show} onHide={close} centered backdrop="static" className="ll-modal">
      <form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title>{title}</Modal.Title>
            {subtitle && <p className="ll-modal__subtitle">{subtitle}</p>}
          </div>
        </Modal.Header>
        <Modal.Body>{children}</Modal.Body>
        <Modal.Footer>
          <span className="ll-modal__note" />
          <div className="ll-modal__actions">
            <button type="button" className="ll-btn ll-btn--secondary" onClick={close} disabled={submitting}>
              Avbryt
            </button>
            <button type="submit" className="ll-btn ll-btn--primary" disabled={submitting}>
              {submitting && <Spinner animation="border" size="sm" />}
              {submitLabel}
            </button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

function Text({ id, label, required, value, onChange, type = 'text', placeholder, ...rest }) {
  return (
    <div className="ll-field">
      <label htmlFor={id}>{label}{required && <Req />}</label>
      <input
        id={id}
        type={type}
        className="ll-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        {...rest}
      />
    </div>
  );
}

export function AddressModal({ show, onClose, onSubmit, form, setForm, isEdit, submitting, customerName }) {
  const set = (key) => (value) => setForm({ ...form, [key]: value });
  return (
    <Frame
      show={show}
      onClose={onClose}
      onSubmit={onSubmit}
      title={isEdit ? 'Redigera adress' : 'Lägg till adress'}
      subtitle={customerName}
      submitLabel={isEdit ? 'Spara ändringar' : 'Lägg till adress'}
      submitting={submitting}
    >
      <div className="ll-field">
        <label htmlFor="addr-type">Adresstyp</label>
        <BrandSelect
          inputId="addr-type"
          name="address_type"
          options={ADDRESS_TYPES}
          value={form.address_type}
          onChange={(e) => set('address_type')(e.target.value)}
        />
      </div>
      <Text id="addr-street1" label="Gatuadress" required value={form.street1} onChange={set('street1')} placeholder="Gatunamn och nummer" />
      <Text id="addr-street2" label="Gatuadress 2" value={form.street2} onChange={set('street2')} placeholder="Lägenhetsnummer, våning, c/o" />
      <div className="ll-field__row ll-field__row--3">
        <Text id="addr-city" label="Ort" required value={form.city} onChange={set('city')} />
        <Text id="addr-postal" label="Postnummer" required value={form.postalcode} onChange={set('postalcode')} placeholder="XXX XX" />
        <Text id="addr-country" label="Land" value={form.country} onChange={set('country')} placeholder="Sverige" />
      </div>
    </Frame>
  );
}

export function ContactModal({ show, onClose, onSubmit, form, setForm, isEdit, submitting, customerName }) {
  const set = (key) => (value) => setForm({ ...form, [key]: value });
  return (
    <Frame
      show={show}
      onClose={onClose}
      onSubmit={onSubmit}
      title={isEdit ? 'Redigera kontaktperson' : 'Lägg till kontaktperson'}
      subtitle={customerName}
      submitLabel={isEdit ? 'Spara ändringar' : 'Lägg till kontaktperson'}
      submitting={submitting}
    >
      <div className="ll-field__row">
        <Text id="contact-name" label="Namn" required value={form.contact_name} onChange={set('contact_name')} />
        <Text id="contact-role" label="Roll" value={form.role} onChange={set('role')} placeholder="T.ex. Ekonomiansvarig" />
      </div>
      <div className="ll-field__row">
        <Text id="contact-email" label="E-post" type="email" value={form.contact_email} onChange={set('contact_email')} />
        <Text id="contact-phone" label="Telefon" type="tel" value={form.contact_phone} onChange={set('contact_phone')} />
      </div>
    </Frame>
  );
}

export function EditCustomerModal({ show, onClose, onSubmit, form, setForm, submitting }) {
  const set = (key) => (value) => setForm({ ...form, [key]: value });
  const isCompany = form.type === 'company';
  const byggDisabled = !isSwedenCountry(form.country || 'Sverige');

  const setCountry = (next) => {
    // Auto-clear the bygg flag when leaving Sverige — the checkbox is disabled
    // then, so a stale `true` would otherwise be invisible-but-set in state.
    setForm({
      ...form,
      country: next,
      is_construction_business: isSwedenCountry(next) ? form.is_construction_business : false,
    });
  };

  return (
    <Frame
      show={show}
      onClose={onClose}
      onSubmit={onSubmit}
      title="Redigera kund"
      subtitle={isCompany ? 'Företag' : 'Privatperson'}
      submitLabel="Spara ändringar"
      submitting={submitting}
    >
      {isCompany ? (
        <>
          <Text id="cust-name" label="Företagsnamn" required value={form.name} onChange={set('name')} />
          <div className="ll-field__row">
            <Text id="cust-orgnr" label="Organisationsnummer" value={form.orgnumber} onChange={set('orgnumber')} />
            <Text id="cust-vat" label="VAT-nummer" value={form.vatnumber} onChange={set('vatnumber')} />
          </div>
        </>
      ) : (
        <div className="ll-field__row">
          <Text id="cust-first" label="Förnamn" required value={form.first_name} onChange={set('first_name')} />
          <Text id="cust-last" label="Efternamn" required value={form.last_name} onChange={set('last_name')} />
        </div>
      )}

      <div className="ll-field__row">
        <Text id="cust-email" label="E-post" type="email" value={form.email} onChange={set('email')} />
        <Text id="cust-phone" label="Telefon" type="tel" value={form.phone} onChange={set('phone')} />
      </div>

      <div className="ll-field__row">
        <div className="ll-field">
          <div className="ll-field__label">
            <label htmlFor="cust-country">Land<Req /></label>
            <InfoPopover ariaLabel="Om Land">
              Land där kunden är registrerad. Avgör om fakturan bär svensk moms, omvänd skattskyldighet (EU) eller faller utanför momsens tillämpningsområde.
            </InfoPopover>
          </div>
          <BrandSelect
            inputId="cust-country"
            name="country"
            options={COUNTRIES}
            value={form.country || 'Sverige'}
            onChange={(e) => setCountry(e.target.value)}
            isSearchable
          />
        </div>
        <Text
          id="cust-terms"
          label="Betalningsvillkor (dagar)"
          type="number"
          min="0"
          value={form.payment_terms}
          onChange={(v) => set('payment_terms')(v === '' ? '' : parseInt(v, 10))}
        />
      </div>

      <div className="ll-field">
          <div className={`ll-check${byggDisabled ? ' is-disabled' : ''}`}>
            <label>
              <input
                type="checkbox"
                checked={Boolean(form.is_construction_business) && !byggDisabled}
                disabled={byggDisabled}
                onChange={(e) => set('is_construction_business')(e.target.checked)}
              />
              Byggföretag — omvänd skattskyldighet inom byggsektorn
            </label>
            <InfoPopover ariaLabel="Om omvänd skattskyldighet inom byggsektorn">
              Markera om kunden är ett byggföretag och omfattas av omvänd skattskyldighet inom byggsektorn (ML 16 kap 13 §).
            </InfoPopover>
          </div>
          {byggDisabled && (
            <span className="ll-field__hint">
              Endast tillämpligt för svenska kunder. För utländska kunder hanteras moms via Land.
            </span>
          )}
        </div>
    </Frame>
  );
}
